'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useTranslations } from 'next-intl'
import { slugify } from '@/lib/slugify'
import {
  Search,
  FileText,
  Shield,
  AlertCircle,
  Loader2,
  Download,
  File,
  Image,
  Video,
  Archive,
  Code,
  Music,
  ArrowLeft
} from 'lucide-react'

interface PublicDocument {
  id: string
  type: string
  displayName: string
  slug: string | null
  icon: string | null
  fileUrl: string
  fileSize: number
  mimeType: string
  inherited?: boolean
}

interface ProductWithDocuments {
  id: string
  name: string
  model?: string | null
  description?: string | null
  productGroupName?: string | null
  hasOwnDocuments?: boolean
  inheritsFromGroup?: boolean
  documents: PublicDocument[]
}

function getDocumentIcon(doc: PublicDocument) {
  if (doc.type === 'ifu') return FileText
  if (doc.type === 'doc') return Shield

  const icon = doc.icon?.toLowerCase()
  if (icon) {
    switch (icon) {
      case 'filetext': case 'file-text': return FileText
      case 'shield': return Shield
      case 'download': return Download
      case 'image': case 'file-image': return Image
      case 'video': case 'file-video': return Video
      case 'archive': return Archive
      case 'code': return Code
      case 'music': case 'file-audio': return Music
      default: break
    }
  }

  if (doc.mimeType) {
    if (doc.mimeType.startsWith('image/')) return Image
    if (doc.mimeType.startsWith('video/')) return Video
    if (doc.mimeType.startsWith('audio/')) return Music
    if (doc.mimeType.includes('zip') || doc.mimeType.includes('archive')) return Archive
  }

  return File
}

function getDocButtonLabel(doc: PublicDocument, t: any) {
  if (doc.type === 'ifu') return t('documents.downloadIfu')
  if (doc.type === 'doc') return t('documents.downloadDoc')
  return doc.displayName
}

interface ProductDocumentsListProps {
  /**
   * When provided, the list is auto-scoped to the product whose slugified
   * model matches this value. The search input is hidden and a "Back to all
   * documents" link is shown instead. Used for deep-links given to the
   * notified body, e.g. /docs/egp-fit.
   */
  modelSlug?: string
}

export function ProductDocumentsList({ modelSlug }: ProductDocumentsListProps = {}) {
  const t = useTranslations('docs')
  const [products, setProducts] = useState<ProductWithDocuments[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const isScoped = !!modelSlug

  useEffect(() => {
    loadProducts()
  }, [])

  const loadProducts = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await fetch('/api/products/public')

      if (response.ok) {
        const data = await response.json()
        setProducts(data.products)
      } else {
        throw new Error('Failed to fetch products')
      }
    } catch (error) {
      setError(t('emptyState.noDocuments'))
      console.error('Error loading products:', error)
    } finally {
      setLoading(false)
    }
  }

  // Resolve the scoped product (if any) by matching the slugified model.
  const scopedProduct = isScoped
    ? products.find((p) => p.model && slugify(p.model) === modelSlug) ?? null
    : null

  // Filter products that have documents. In scoped mode, restrict to the
  // single matched product; otherwise honour the search input.
  const filteredProducts = isScoped
    ? scopedProduct && scopedProduct.documents.length > 0
      ? [scopedProduct]
      : []
    : products.filter(product =>
        product.documents.length > 0 &&
        (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         (product.model && product.model.toLowerCase().includes(searchTerm.toLowerCase())))
      )

  // Group products by their document fingerprint to merge products with identical documents
  const groupedProducts = filteredProducts.reduce((groups, product) => {
    const docsKey = product.documents.map(d => d.id).sort().join(',')
    const key = docsKey || product.id

    if (!groups[key]) {
      groups[key] = {
        ...product,
        products: [product]
      }
    } else {
      groups[key].products.push(product)
      if (groups[key].products.length === 2) {
        groups[key].name = groups[key].productGroupName || `${groups[key].name} Series`
        groups[key].model = null
      }
    }

    return groups
  }, {} as Record<string, any>)

  const displayProducts = Object.values(groupedProducts)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-muted-foreground">{t('emptyState.noDocuments')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {isScoped ? (
        scopedProduct ? (
          // Product-scoped header (match found): replaces the search input.
          // Shown when the page is opened via /docs/{model-slug}.
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium">
              {t('productView.showingFor', {
                product: scopedProduct.model || scopedProduct.name,
              })}
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link href="/docs" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('productView.backToAll')}
              </Link>
            </Button>
          </div>
        ) : (
          // Product-scoped not-found: a proper centered empty state that
          // mirrors the search empty state below, instead of cramming the
          // message and back button into a header row.
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('productView.notFound', { slug: modelSlug! })}
            </h3>
            <p className="text-muted-foreground mb-6">
              {t('emptyState.noDocumentsDescription')}
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link href="/docs" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('productView.backToAll')}
              </Link>
            </Button>
          </div>
        )
      ) : (
        // Default search input.
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Products Grid */}
      <div className="grid gap-4">
        {displayProducts.map(productGroup => (
          <Card key={productGroup.products.map((p: any) => p.id).join('-')}>
            <CardHeader>
              <CardTitle>
                {productGroup.model || productGroup.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {productGroup.documents.map((doc: PublicDocument) => {
                  const IconComponent = getDocumentIcon(doc)
                  const href = doc.slug ? `/d/${doc.slug}` : doc.fileUrl
                  return (
                    <Button key={doc.id} variant="outline" asChild>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <IconComponent className="w-4 h-4" />
                        {getDocButtonLabel(doc, t)}
                      </a>
                    </Button>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty States — suppressed in scoped mode because the header above
          already communicates the not-found state explicitly. */}
      {displayProducts.length === 0 && !loading && !isScoped && (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
            {searchTerm ? (
              <Search className="w-8 h-8 text-muted-foreground" />
            ) : (
              <FileText className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
          <h3 className="text-lg font-medium mb-2">
            {searchTerm ? t('emptyState.noSearchResults') : t('emptyState.noDocuments')}
          </h3>
          <p className="text-muted-foreground">
            {searchTerm
              ? t('emptyState.noSearchResultsDescription')
              : t('emptyState.noDocumentsDescription')
            }
          </p>
          {searchTerm && (
            <Button
              variant="outline"
              onClick={() => setSearchTerm('')}
              className="mt-4"
            >
              {t('search.clearSearch')}
            </Button>
          )}
        </div>
      )}

      {/* Info Footer */}
      {displayProducts.length > 0 && (
        <div className="text-center text-sm text-muted-foreground border-t pt-6">
          <p>
            {t('footer.showing', {
              count: displayProducts.length,
              plural: displayProducts.length !== 1 ? 's' : ''
            })}
          </p>
          <p className="mt-1">
            {t('footer.compliance')}
          </p>
        </div>
      )}
    </div>
  )
}
